/**
 * Modal — diálogo superpuesto del tema dark "torre de control".
 *
 * Presentacional puro: el PADRE controla la visibilidad con `@if` y reacciona
 * al output `closed` (backdrop, botón ✕ o Escape). El cuerpo se proyecta como
 * contenido default y las acciones en el slot `[slot=footer]`.
 *
 *   @if (abierto()) {
 *     <pvp-modal title="Confirmar acción" (closed)="abierto.set(false)">
 *       <p>Cuerpo…</p>
 *       <ng-container ngProjectAs="[slot=footer]">…botones…</ng-container>
 *     </pvp-modal>
 *   }
 *
 * A11y: role=dialog + aria-modal + aria-labelledby, foco inicial al contenedor
 * y cierre con Escape (listener a nivel documento mientras está montado).
 */
import type {
  AfterViewInit,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';

import { IconComponent } from '../../atoms/icon/icon.component';

export type ModalSize = 'md' | 'lg';

const MODAL_MAX_WIDTH: Record<ModalSize, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
};

let nextModalId = 0;

@Component({
    selector: 'pvp-modal',
    imports: [IconComponent, CdkTrapFocus],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown.escape)': 'closed.emit()',
    },
    template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <!-- Backdrop: click afuera = cerrar -->
      <div
        class="absolute inset-0 bg-black/60"
        aria-hidden="true"
        (click)="closed.emit()"
      ></div>

      <div
        #dialog
        role="dialog"
        aria-modal="true"
        cdkTrapFocus
        [attr.aria-labelledby]="titleId"
        tabindex="-1"
        [class]="panelClass()"
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
          <h2 [id]="titleId" class="m-0 text-base font-semibold text-text">{{ title() }}</h2>
          <button
            type="button"
            class="shrink-0 rounded-sm p-1 text-text-muted transition-colors duration-150 hover:bg-surface-raised hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Cerrar"
            (click)="closed.emit()"
          >
            <pvp-icon name="x" size="sm" />
          </button>
        </header>

        <div class="overflow-y-auto px-5 py-4">
          <ng-content />
        </div>

        <footer class="flex justify-end gap-2 border-t border-border px-5 py-4">
          <ng-content select="[slot=footer]" />
        </footer>
      </div>
    </div>
  `
})
export class ModalComponent implements AfterViewInit, OnDestroy {
  /** Título del diálogo (también lo referencia aria-labelledby). */
  readonly title = input.required<string>();

  /** Ancho máximo del panel: 'md' para confirmaciones, 'lg' para formularios. */
  readonly size = input<ModalSize>('lg');

  /** El usuario pidió cerrar (backdrop, ✕ o Escape) — el padre decide. */
  readonly closed = output<void>();

  protected readonly titleId = `pvp-modal-title-${nextModalId++}`;

  protected readonly panelClass = computed(
    () =>
      'relative flex max-h-full w-full flex-col rounded-lg border border-border bg-surface shadow-xl focus:outline-none ' +
      MODAL_MAX_WIDTH[this.size()],
  );

  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');

  /**
   * Elemento que tenía el foco ANTES de abrir el modal (el disparador). Se captura
   * en la construcción — cuando el padre monta el modal con `@if`, el foco todavía
   * está en el trigger — para devolvérselo al cerrar (WCAG 2.4.3, focus order).
   */
  private readonly previouslyFocused =
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;

  ngAfterViewInit(): void {
    // Foco inicial al contenedor para que Escape/Tab arranquen dentro del diálogo.
    // `cdkTrapFocus` mantiene el Tab CICLANDO dentro del diálogo (lo que exige
    // aria-modal="true": el foco de teclado no debe escaparse al fondo inerte).
    this.dialog().nativeElement.focus();
  }

  ngOnDestroy(): void {
    // Al desmontarse (el padre cerró el diálogo) devuelve el foco al disparador,
    // para no dejar al usuario de teclado tirado arriba de la página.
    this.previouslyFocused?.focus?.();
  }
}
